import { fillAssetFinanceCompareProduct } from '__core/shared/util/salestrekker';
/* @ngInject */

export default (
  $q,
  $rootScope,
  logger,
  dataService,
  dateTimeService,
  helperService,
  Address,
  ticketFactFindService,
  contactService,
  contactsService,
  financeService,
  financesService,
  Contact,
  AssetFinance,
  AssetFinanceDetail,
  AssetFinanceCompareProduct,
  AssetFinanceInsurance,
  AssetFinanceInsuranceType,
  AssetFinanceProposalAssociatedFee,
  AssetFinanceProposalAssociatedFeeType,
  AssetFinanceAssetCommitmentSchedule,
  AssetFinanceAssetCommitment,
  AssetFinanceProposalAmortizationSchedule,
  PaymentFrequency,
  uiService,
  ASSET_FINANCE
) => {
  /**
   * Build current document from model.
   *
   * @param {Object} document
   *
   * @returns {Object}
   */
  const buildObject = (document) =>
    document instanceof AssetFinance ? document : AssetFinance.build(document);

  /**
   * Create Asset Finance.
   *
   * @param {Object} document
   *
   * @returns {Object} - Created Document ID.
   */
  const createAssetFinance = (document) => {
    const deferred = $q.defer();
    logger.log('assetFinanceService', 'createAssetFinance', 'Resolving DataService');
    dataService.createDocument(AssetFinance, buildObject(document)).then((id) => {
      logger.log(`assetFinanceService | createAssetFinance | Resolved ${id}`);
      deferred.resolve(id);
    });
    return deferred.promise;
  };

  /**
   * Read Asset Finance.
   *
   * @param {Object} document
   *
   * @returns {Object} - Read Document.
   */
  const readAssetFinance = (document) => {
    const deferred = $q.defer();
    logger.log('assetFinanceService', 'readAssetFinance', 'Resolving DataService');
    const loan = document;
    loan.docType = AssetFinance.blank().docType;
    dataService.readDocument(AssetFinance, loan).then((data) => {
      const assetFinance = buildObject(data);
      logger.log(`assetFinanceService | readAssetFinance | Resolved ${assetFinance.id}`);
      deferred.resolve(assetFinance);
    });
    return deferred.promise;
  };

  /**
   * Update Asset Finance.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been Updated?
   */
  const updateAssetFinance = (document) => {
    const deferred = $q.defer();
    logger.log('assetFinanceService', 'updateAssetFinance', 'Resolving DataService');
    dataService.updateDocument(AssetFinance, buildObject(document)).then((updated) => {
      logger.log(`assetFinanceService | updateAssetFinance | Resolved ${updated}`);
      deferred.resolve(updated);
    });
    return deferred.promise;
  };

  /**
   * Update Asset Finance with partial data.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been Updated?
   */
  const updateAssetFinancePartial = (document) => {
    const deferred = $q.defer();
    logger.log('assetFinanceService', 'updateAssetFinancePartial', 'Resolving DataService');
    const assetFinance = document;
    assetFinance.docType = AssetFinance.blank().docType;
    dataService.updateDocument(AssetFinance, assetFinance).then((updated) => {
      logger.log(`assetFinanceService | updateAssetFinancePartial | Resolved ${updated}`);
      deferred.resolve(updated);
    });
    return deferred.promise;
  };

  /**
   * Delete Asset Finance.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been deleted?
   */
  const deleteAssetFinance = (document) => {
    const deferred = $q.defer();
    logger.log('assetFinanceService', 'deleteAssetFinance', 'Resolving DataService');
    const assetFinance = document;
    assetFinance.docType = AssetFinance.blank().docType;
    dataService.deleteDocument(AssetFinance, assetFinance).then((deleted) => {
      logger.log(`assetFinanceService | deleteAssetFinance | Resolved ${deleted}`);
      deferred.resolve(deleted);
    });
    return deferred.promise;
  };

  /**
   * Get available asset types.
   *
   * @returns {Array}
   */
  const getAvailableAssetTypes = (isCommercial, isSearch) => {
    const assetTypes = _.sortBy(
      ASSET_FINANCE.DETAILS.ASSET_TYPE.filter(
        (t) =>
          (isCommercial
            ? (!t.isConsumer && t.isCommercial) || (t.isConsumer && t.isCommercial)
            : (t.isConsumer && !t.isCommercial) || (t.isConsumer && t.isCommercial)) &&
          (isSearch ? t.value !== 'Select one' : t.value !== 'Not essential')
      ),
      'weight'
    );
    return assetTypes;
  };

  /**
   * Get available asset ages.
   *
   * @returns {Array}
   */
  const getAvailableAssetAges = () => _.sortBy(ASSET_FINANCE.DETAILS.ASSET_AGE, 'weight');

  /**
   * Get available car types.
   *
   * @returns {Array}
   */
  const getAvailableCarTypes = () => _.sortBy(ASSET_FINANCE.DETAILS.CAR_TYPE, 'weight');

  /**
   * Get available vendor types.
   *
   * @param {Boolean} isSearch
   * @param {Boolean} isCommercial
   *
   * @returns {Array}
   */
  const getAvailableVendorTypes = ({ isSearch, isCommercial }) => {
    const availableVendorTypes = _.filter(
      ASSET_FINANCE.DETAILS.VENDOR_TYPE,
      (a) =>
        (isSearch ? a?.value !== 'Select one' : a?.value !== 'Not essential') &&
        (isCommercial ? a?.isCommercial : a?.isConsumer)
    );
    return _.sortBy(availableVendorTypes, 'weight');
  };

  /**
   * Get available product types.
   *
   * @param {Boolean} isCommercial
   * @param {Boolean} isQuickTool
   *
   * @returns {Array}
   */
  const getAvailableProductTypes = ({ isCommercial, isQuickTool }) =>
    _.sortBy(
      ASSET_FINANCE.DETAILS.PRODUCT_TYPE.filter(
        (a) =>
          (isCommercial ? a.isCommercial : a.isConsumer) &&
          (isQuickTool ? a.isQuickTool === isQuickTool : true)
      ),
      'weight'
    );

  /**
   * Get available asset finance insurance product types.
   *
   * @returns {Array}
   */
  const getAssetFinanceInsuranceProductTypes = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.INSURANCE_PRODUCT_TYPE, 'weight');

  /**
   * Get available asset finance interest rates.
   *
   * @returns {Array}
   */
  const getAssetFinanceInterestRates = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.INTEREST_RATES, 'id');

  /**
   * Get available asset finance interest rates.
   *
   * @returns {Array}
   */
  const getAssetFinanceCreditRecords = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.CREDIT_RECORD, 'id');

  /**
   * Get available payment frequencies.
   *
   * @returns {Array}
   */
  const getAvailablePaymentFrequencies = (isCommercial) => {
    const paymentFrequencies = _.sortBy(
      ASSET_FINANCE.DETAILS.PAYMENT_FREQUENCY.filter((t) =>
        isCommercial
          ? (!t.isConsumer && t.isCommercial) || (t.isConsumer && t.isCommercial)
          : (t.isConsumer && !t.isCommercial) || (t.isConsumer && t.isCommercial)
      ),
      'weight'
    );
    return paymentFrequencies;
  };

  /**
   * Get available search asset age.
   *
   * @returns {Array}
   */
  const getAvailableSearchAssetAges = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.ASSET_AGE, 'weight');

  /**
   * Get available gross vehicle mass.
   *
   * @returns {Array}
   */
  const getAvailableGrossVehicleMass = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.GROSS_VEHICLE_MASS, 'weight');

  /**
   * Get available payload.
   *
   * @returns {Array}
   */
  const getAvailablePayLoad = () => _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.PAY_LOAD, 'weight');

  /**
   * Get available pax.
   *
   * @returns {Array}
   */
  const getAvailablePax = () => _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.PAX, 'weight');

  /**
   * Get available loan types.
   *
   * @returns {Array}
   */
  const getAvailableLoanTypes = (isCommercial) => {
    const lonTypes = _.sortBy(
      _.filter(ASSET_FINANCE.DETAILS.SEARCH.LOAN_TYPES, (t) =>
        isCommercial
          ? (!t.isConsumer && t.isCommercial) || (t.isConsumer && t.isCommercial)
          : (t.isConsumer && !t.isCommercial) || (t.isConsumer && t.isCommercial)
      ),
      'weight'
    );
    return lonTypes;
  };

  /**
   * Get available yes/no option.
   *
   * @returns {Array}
   */
  const getAvailableYesNoOption = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.YES_NO_OPTION, 'weight');

  /**
   * Get available elapsed time from payout.
   *
   * @returns {Array}
   */
  const getAvailableElapsedTimeFromPayout = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.ELAPSED_TIME_FROM_PAYOUT, 'weight');

  /**
   * Get available elapsed term old loan.
   *
   * @returns {Array}
   */
  const getAvailableElapsedTermOldLoan = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.ELAPSED_TERM_OLD_LOAN, 'weight');

  /**
   * Get available increase in repayment.
   *
   * @returns {Array}
   */
  const getAvailableIncreaseInRepayment = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.INCREASE_IN_REPAYMENT, 'weight');

  /**
   * Get available employment stabilities.
   *
   * @returns {Array}
   */
  const getAvailableAssetFinanceEmploymentBasis = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.EMPLOYMENT_BASIS, 'weight');

  /**
   * Get available time at current address.
   *
   * @returns {Array}
   */
  const getAvailableTimeAtCurrentAddress = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.TIME_AT_CURRENT_ADDRESS, 'weight');

  /**
   * Get available credit histories.
   *
   * @param {Boolean} isCommercial
   *
   * @returns {Array}
   */
  const getAvailableCreditHistories = (isCommercial) => {
    const paymentFrequencies = _.sortBy(
      ASSET_FINANCE.DETAILS.SEARCH.CREDIT_HISTORY.filter((c) =>
        isCommercial ? c.isCommercial : c.isConsumer
      ),
      'weight'
    );
    return paymentFrequencies;
  };

  /**
   * Get available residential status.
   *
   * @returns {Array}
   */
  const getAvailableResidentialStatus = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.RESIDENTIAL_STATUS, 'weight');

  /**
   * Get available ABN registered.
   *
   * @returns {Array}
   */
  const getAvailableAbnRegistered = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.ABN_REGISTERED, 'weight');

  /**
   * Get available GST registered.
   *
   * @returns {Array}
   */
  const getAvailableGstRegistered = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.GST_REGISTERED, 'weight');

  /**
   * Get available home ownership.
   *
   * @returns {Array}
   */
  const getAvailableHomeOwnership = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.HOME_OWNERSHIP, 'weight');

  /**
   * Get available time in employment
   *
   * @returns {Array}
   */
  const getAvailableTimeInEmployment = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.TIME_IN_EMPLOYMENT, 'weight');

  /**
   * Get available time in employment
   *
   * @returns {Array}
   */
  const getAvailableBallonPaymentDebtOptions = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.BALLOON_PAYMENT_DEBT_OPTIONS, 'weight');

  /**
   * Get available pay day enquires
   *
   * @returns {Array}
   */
  const getAvailableBankrupts = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.BANKRUPTS, 'weight');

  /**
   * Get available financial defaults
   *
   * @returns {Array}
   */
  const getAvailableFinancialDefaults = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.FINANCIAL_DEFAULTS, 'weight');

  /**
   * Get available utility and tel co
   *
   * @returns {Array}
   */
  const getAvailableUtilityAndTelCo = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.UTILITY_AND_TEL_CO, 'weight');

  /**
   * Get available pay day enquires
   *
   * @returns {Array}
   */
  const getAvailablePayDayEnquires = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.PAY_DAY_ENQUIRES, 'weight');

  /**
   * Get available pay day on statements
   *
   * @returns {Array}
   */
  const getAvailablePayDayOnStatements = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.PAY_DAY_ON_STATEMENTS, 'weight');

  /**
   * Get available deposit required
   *
   * @returns {Array}
   */
  const getAvailableDepositRequired = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.DEPOSIT_REQUIRED, 'weight');

  /**
   * Get available deposit required
   *
   * @returns {Array}
   */
  const getAvailableMaxNegativeEquity = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.SEARCH.MAX_NEGATIVE_EQUITY, 'weight');

  /**
   * Get available asset finance insurance types.
   *
   * @returns {Array}
   */
  const getAvailableAssetFinanceInsuranceTypes = () => AssetFinanceInsuranceType.available;

  /**
   * Get available asset finance insurance types.
   *
   * @param {Boolean} isQuickTool
   *
   * @returns {Array}
   */
  const getAvailableAssetFinanceAssociatedFeeTypes = ({ isQuickTool }) => {
    let availableAssociatedFees = AssetFinanceProposalAssociatedFeeType.available;
    if ($rootScope.isDev() || $rootScope.isNlg()) {
      return isQuickTool
        ? availableAssociatedFees
        : _.reject(availableAssociatedFees, (_, index) => index === 6);
    }
    availableAssociatedFees = _.reject(
      availableAssociatedFees,
      (_, index) => index === 7 || index === 8
    );
    return isQuickTool
      ? availableAssociatedFees
      : _.reject(availableAssociatedFees, (_, index) => index === 6);
  };

  /**
   * Get product type.
   *
   * @param {Number} productType
   * @param {Boolean} isCommercial
   * @param {Boolean} isQuickTool
   *
   * @returns {String}
   */
  const getProductType = ({ productType, isCommercial, isQuickTool }) => {
    const availableProductTypes = getAvailableProductTypes({ isCommercial, isQuickTool });
    return _.find(availableProductTypes || [], (p) => p?.id === productType);
  };

  /**
   * Get loan term.
   *
   * @param {Number} term
   *
   * @returns {String}
   */
  const getLoanTerm = ({ term }) => {
    const availableTermInMonths = helperService.getAvailableTermInMonths({
      isRegular: true
    });
    return _.find(availableTermInMonths || [], (t) => t?.value === term);
  };

  /**
   * Add new asset to asset details array.
   *
   * @param {Array} assetDetails
   * @param {Array} quickQuoteAssetDetails
   *
   * @returns {Array}
   */
  const assetDetailsAdd = ({ assetDetails, quickQuoteAssetDetails }) => {
    if (quickQuoteAssetDetails?.length) {
      for (let a = 0; a < quickQuoteAssetDetails.length; a += 1) {
        assetDetails.push(AssetFinanceDetail.Build(quickQuoteAssetDetails[a]));
      }
    } else {
      assetDetails.push(AssetFinanceDetail.Blank());
    }
    uiService.toast({
      text: {
        p1: 'Asset',
        p2: `to be financed #${assetDetails?.length}`,
        p3: 'has been added'
      },
      success: true
    });
    return assetDetails;
  };

  /**
   * Add new additional product to asset addtional products array.
   *
   * @param {Array} additionalProducts
   * @param {Object} insuranceType
   *
   * @returns {Array}
   */
  const additionalProductsAdd = (additionalProducts, insuranceType) => {
    additionalProducts.push(AssetFinanceInsurance.Blank(insuranceType));
    uiService.toast({
      text: {
        p1: insuranceType.getName(),
        p2: 'additional product',
        p3: 'has been added'
      },
      success: true
    });
    return additionalProducts;
  };

  /**
   * Add new associated fee to finance proposal associated fees array.
   *
   * @param {Array} associatedFees
   * @param {Object} feeType
   * @param {Boolean} showToast
   *
   * @returns {Array}
   */
  const financeProposalAssociatedFeeAdd = ({ associatedFees, feeType, showToast }) => {
    const associatedFee = AssetFinanceProposalAssociatedFee.Blank(feeType);
    associatedFee.name =
      feeType.id !== getAvailableAssetFinanceAssociatedFeeTypes({})[4].id
        ? feeType.getName()
        : '';
    associatedFees.push(associatedFee);
    if (showToast) {
      uiService.toast({
        text: {
          p1: 'Associated fee',
          p2: feeType.name,
          p3: 'has been added'
        },
        success: true
      });
    }
    return associatedFees;
  };

  /**
   * Add new asset commitment schedule.
   *
   * @param {Array} assetCommitmentSchedules
   *
   * @returns {Array}
   */
  const assetCommitmentScheduleAdd = (assetCommitmentSchedules) => {
    assetCommitmentSchedules.push(AssetFinanceAssetCommitmentSchedule.blank());
    return assetCommitmentSchedules;
  };

  /**
   * Add new asset commitment.
   *
   * @param {Array} assetCommitments
   *
   * @returns {Array}
   */
  const assetCommitmentAdd = (assetCommitments) => {
    assetCommitments.push(AssetFinanceAssetCommitment.blank());
    return assetCommitments;
  };

  /**
   * Get asset commitment schedule total.
   *
   * @param {Array} assetCommitments
   * @param {String} field
   *
   * @returns {Object}
   */
  const getAssetCommitmentScheduleTotal = ({ assetCommitments, field }) => {
    let total = 0;
    if (field && assetCommitments) {
      total = _.reduce(
        assetCommitments,
        (memo, num) => memo + (num.toBePaidOut ? 0 : helperService.isNumber(num[field], true)),
        0
      );
    }
    return total;
  };

  /**
   * Change vendor details.
   *
   * @param {Object} assetToUpdate
   * @param {Object} selectedContact
   *
   * @returns {Object}
   */
  const changeSelectedItemOfVendor = ({ assetToUpdate, selectedContact }) => {
    const deferred = $q.defer();
    const asset = assetToUpdate;
    const idContact = selectedContact?.id || asset?.vendor?.idContact;
    if (idContact && asset?.vendor) {
      contactService.readContact({ id: idContact }).then((data) => {
        if (data) {
          const contact = data;
          asset.vendor.idContact = contact?.id;
          asset.vendor.name = contact?.company?.information?.name || '';
          asset.vendor.entityNumber = contact?.company?.information?.refNumber || '';
          asset.vendor.phoneCode = contact?.company?.contact?.phoneCode || '';
          asset.vendor.phone = contact?.company?.contact?.phone || '';
          asset.vendor.faxCode = contact?.company?.contact?.faxCode || '';
          asset.vendor.fax = contact?.company?.contact?.fax || '';
          asset.vendor.email = contact?.company?.contact?.email || '';
          asset.vendor.contactTitle =
            contact?.company?.information?.individuals[0]?.title || '';
          asset.vendor.contactFirstName =
            contact?.company?.information?.individuals[0]?.firstName || '';
          asset.vendor.contactSurname =
            contact?.company?.information?.individuals[0]?.familyName || '';
          asset.vendor.address.formatted = contact?.company?.contact?.address?.formatted || '';
          asset.vendor.address.placeId = contact?.company?.contact?.address?.placeId || '';
          asset.vendor.address.suiteNumber =
            contact?.company?.contact?.address?.suiteNumber || '';
          asset.vendor.address.streetNumber =
            contact?.company?.contact?.address?.streetNumber || '';
          asset.vendor.address.street = contact?.company?.contact?.address?.street || '';
          asset.vendor.address.suburb = contact?.company?.contact?.address?.suburb || '';
          asset.vendor.address.city = contact?.company?.contact?.address?.city || '';
          asset.vendor.address.state = contact?.company?.contact?.address?.state || '';
          asset.vendor.address.countryCode =
            contact?.company?.contact?.address?.countryCode || '';
          asset.vendor.address.country = contact?.company?.contact?.address?.country || '';
          asset.vendor.address.latitude = contact?.company?.contact?.address?.latitude || 0;
          asset.vendor.address.longitude = contact?.company?.contact?.address?.longitude || 0;
          asset.vendor.address.postCode = contact?.company?.contact?.address?.postCode || '';
          asset.vendor.address.district = contact?.company?.contact?.address?.district || '';
        }
        deferred.resolve(asset);
      });
    } else {
      deferred.resolve(asset);
    }
    return deferred.promise;
  };

  /**
   * Get upfront fee.
   *
   * @param {Object} selectedProduct
   * @param {Array} associatedFees
   *
   * @returns {Number}
   */
  const getUpfrontFee = (selectedProduct, associatedFees) => {
    let upfront = 0;
    if (
      selectedProduct &&
      Object.keys(selectedProduct).length !== 0 &&
      associatedFees &&
      associatedFees.length
    ) {
      upfront = associatedFees.reduce(
        (memo, num) => memo + helperService.isNumber(num.amountWithGst, true),
        0
      );
    }
    return upfront;
  };

  /**
   * Get monthly fee.
   *
   * @param {Object} selectedProduct
   *
   * @returns {Number}
   */
  const getMonthlyFee = (selectedProduct) => {
    let monthlyFee = 0;
    if (
      selectedProduct &&
      Object.keys(selectedProduct).length !== 0 &&
      selectedProduct.fee &&
      selectedProduct.fee.length
    ) {
      const fee = selectedProduct.fee.find((f) => f.type === 'MONTHLY');
      monthlyFee = fee ? fee.value : 0;
    }
    return monthlyFee;
  };

  /**
   * Get compound.
   *
   * @param {Object} paymentFrequency
   *
   * @returns {Number}
   */
  const getCompound = ({ paymentFrequency }) => {
    let compound = 1;
    if (
      paymentFrequency?.name === PaymentFrequency.quarterly.name ||
      paymentFrequency?.name === PaymentFrequency.annually.name
    ) {
      compound = paymentFrequency?.name === PaymentFrequency.quarterly.name ? 3 : 12;
    }
    return compound;
  };

  /**
   * Get compound.
   *
   * @param {Object} paymentFrequency
   * @param {Number} interestRate
   *
   * @returns {Number}
   */
  const getRate = ({ paymentFrequency, interestRate }) => {
    let rate = 0;
    if (interestRate) {
      if (
        paymentFrequency?.name === PaymentFrequency.monthly.name ||
        paymentFrequency?.name === PaymentFrequency.quarterly.name ||
        paymentFrequency?.name === PaymentFrequency.annually.name
      ) {
        const compound = getCompound({ paymentFrequency });
        rate = _.subtract(_.add(_.divide(_.divide(interestRate, 100), 12), 1) ** compound, 1);
      } else {
        rate = _.divide(
          _.divide(interestRate, 100),
          helperService.getFrequencyValue(paymentFrequency)
        );
      }
    }
    return rate;
  };

  /**
   * Get net amount financed.
   *
   * @param {Number} netAmountFinanced
   * @param {Number} brokerCommissionPercent
   *
   * @returns {Number}
   */
  const getNetAmountFinanced = ({ netAmountFinanced, brokerCommissionPercent }) =>
    brokerCommissionPercent !== 0
      ? netAmountFinanced + netAmountFinanced * (brokerCommissionPercent / 100)
      : netAmountFinanced;

  /**
   * Get current net amount financed.
   *
   * @param {Array} assetDetails
   * @param {Object} financeProposal
   *
   * @returns {Number}
   */
  const getCurrentNetAmountFinanced = ({ assetDetails, financeProposal }) => {
    let total = _.reduce(
      assetDetails,
      (memo, num) => memo + helperService.isNumber(num.netCost, true),
      0
    );
    if (financeProposal?.netAmountFinanced > 0) {
      total = financeProposal.netAmountFinanced;
    }
    return total;
  };

  /**
   * Get number of payment periods.
   *
   * @param {Number} term
   * @param {Object} paymentFrequency
   *
   * @returns {Number}
   */
  const getNumberOfPaymentPeriods = ({ term, paymentFrequency }) =>
    term && paymentFrequency?.name !== PaymentFrequency.none.name
      ? _.divide(_.multiply(term, helperService.getFrequencyValue(paymentFrequency)), 12)
      : 0;

  /**
   * Get rate without compound.
   *
   * @param {Number} interestRate
   * @param {Object} paymentFrequency
   *
   * @returns {Number}
   */
  const getRateWithoutCompound = ({ interestRate, paymentFrequency }) =>
    interestRate && paymentFrequency?.name !== PaymentFrequency.none.name
      ? _.divide(_.divide(interestRate, 100), helperService.getFrequencyValue(paymentFrequency))
      : 0;

  /**
   * Get preferred product.
   *
   * @param {Array} compareProducts
   *
   * @returns {Object}
   */
  const getPreferredProduct = (compareProducts) =>
    _.find(compareProducts, (c) => c?.isPreferred);

  /**
   * Get product total fees.
   *
   * @param {Object} product
   * @param {Number} term
   *
   * @returns {Number}
   */
  const getProductTotalFees = (product, term) =>
    _.reduce(
      product.fee,
      (memo, num) =>
        num.type === 'MONTHLY'
          ? memo + helperService.isNumber(num.value, true) * helperService.isNumber(term, false)
          : memo + helperService.isNumber(num.value, true),
      0
    );

  /**
   * Is ANZ selected as a lender.
   *
   * @param {Object} product
   *
   * @returns {Boolean}
   */
  const isAnzSelectedAsLender = ({ product, isQuickTool }) => {
    if (isQuickTool) {
      return product?.lender?.length ? product?.lender[0]?.abbr === 'ANZAF' : false;
    }
    return product?.selectedProduct?.lender?.length
      ? product?.selectedProduct?.lender[0]?.abbr === 'ANZAF'
      : false;
  };

  /**
   * Is MACQ selected as a lender.
   *
   * @param {Object} product
   *
   * @returns {Boolean}
   */
  const isMacqSelectedAsLender = ({ product, isQuickTool }) => {
    if (isQuickTool) {
      return product?.lender?.length ? product?.lender[0]?.abbr === 'MLAF' : false;
    }
    return product?.selectedProduct?.lender?.length
      ? product?.selectedProduct?.lender[0]?.abbr === 'MLAF'
      : false;
  };

  /**
   * Is MACQ selected as a lender.
   *
   * @param {Object} product
   * @param {Object} feeType
   * @param {Boolean} isQuickTool
   *
   * @returns {Boolean}
   */
  const isMacqSelectedAsLenderAndFeeTypeMarkedAsPpsr = ({ product, feeType, isQuickTool }) =>
    isMacqSelectedAsLender({ product, isQuickTool }) && feeType?.id?.toLowerCase() === 'ppsr';

  /**
   * Is ANZ selected as a lender.
   *
   * @param {Object} product
   * @param {Object} financeProposal
   * @param {Boolean} isQuickTool
   *
   * @returns {Boolean}
   */
  const isProductTypeLeaseAndAnzLender = ({ product, financeProposal, isQuickTool }) =>
    isAnzSelectedAsLender({ product, isQuickTool }) && isQuickTool
      ? financeProposal?.productType === 5 ||
        financeProposal?.productType === 6 ||
        financeProposal?.productType === 7
      : product?.productDetails?.productType === 2 ||
        product?.productDetails?.productType === 3 ||
        product?.productDetails?.productType === 7;

  /**
   * Show editable fields.
   *
   * @param {Object} product
   * @param {Object} financeProposal
   *
   * @returns {Boolean}
   */
  const showEditableFields = ({ product, financeProposal }) => {
    if (product && financeProposal) {
      return (
        (isProductTypeLeaseAndAnzLender({ product }) &&
          (product?.productDetails?.productType === 2 ||
            product?.productDetails?.productType === 3)) ||
        product?.productDetails?.productType === 5 ||
        product?.productDetails?.productType === 6 ||
        product?.productDetails?.productType === 7 ||
        financeProposal?.productType === 5 ||
        financeProposal?.productType === 6 ||
        financeProposal?.productType === 7
      );
    }
    if (product) {
      return (
        (isProductTypeLeaseAndAnzLender({ product }) &&
          (product?.productDetails?.productType === 2 ||
            product?.productDetails?.productType === 3)) ||
        product?.productDetails?.productType === 5 ||
        product?.productDetails?.productType === 6 ||
        product?.productDetails?.productType === 7
      );
    }
    if (financeProposal) {
      return (
        financeProposal?.productType === 5 ||
        financeProposal?.productType === 6 ||
        financeProposal?.productType === 7
      );
    }
    return false;
  };

  /**
   * Can select lease.
   *
   * @param {Array} assetDetails
   *
   * @returns {Boolean}
   */
  const canSelectLease = (assetDetails) => {
    if (assetDetails) {
      return _.every(assetDetails, (a) => !a.deposit && !a.isTrade);
    }
    return true;
  };

  /**
   * Is lease selected as product type.
   *
   * @param {Object} item
   * @param {Boolean} isAssetDetails
   *
   * @returns {Boolean}
   */
  const isLeaseSelectedAsProductType = ({ item, isAssetDetails }) => {
    if (isAssetDetails) {
      return (
        item?.productType === 2 ||
        item?.productType === 3 ||
        item?.productType === 5 ||
        item?.productType === 7
      );
    }
    return item?.productType === 2 || item?.productType === 3 || item?.productType === 7;
  };

  /**
   * Show asset detail mileage.
   *
   * @param {Number} idAssetType
   * @param {Boolean} isCommercial
   * @param {Boolean} isMileage
   *
   * @returns {Boolean}
   */
  const showAssetDetailMileageOrHours = ({ assetType, isCommercial, isMileage }) => {
    const availableAssetTypes = getAvailableAssetTypes(isCommercial, false);
    return (
      _.find(
        availableAssetTypes,
        (a) => assetType === a.id && (isMileage ? a.showMileage : a.showHours)
      ) !== undefined
    );
  };

  /**
   * Toggle asset detail type.
   *
   * @param {Object} assetDetail
   * @param {Boolean} isCommercial
   *
   * @returns {Object}
   */
  const toggleAssetDetailType = ({ assetDetail, isCommercial }) => {
    const asset = assetDetail;
    if (
      showAssetDetailMileageOrHours({ assetType: asset.type, isCommercial, isMileage: true })
    ) {
      asset.vehicle.hours = 0;
    } else if (
      showAssetDetailMileageOrHours({ assetType: asset.type, isCommercial, isMileage: false })
    ) {
      asset.vehicle.mileage = 0;
    } else {
      asset.vehicle.hours = 0;
      asset.vehicle.mileage = 0;
    }
    return asset;
  };

  /**
   * Toggle asset detail trade.
   *
   * @param {Object} assetDetail
   *
   */
  const toggleAssetDetailTrade = ({ assetDetail }) => {
    const asset = assetDetail;
    if (!asset.isTrade) {
      asset.trade.description = '';
      asset.trade.value = 0;
      asset.trade.lender = '';
      asset.trade.lenderOtherName = '';
      asset.trade.payOutOn = 0;
      asset.trade.balance = 0;
      asset.trade.isPayout = false;
    }
    return asset;
  };

  /**
   * Toggle asset detail payout.
   *
   * @param {Object} assetDetail
   *
   */
  const toggleAssetDetailPayout = ({ assetDetail }) => {
    const asset = assetDetail;
    if (!asset.trade.isPayout) {
      asset.trade.lender = '';
      asset.trade.lenderOtherName = '';
      asset.trade.payOutOn = 0;
      asset.trade.balance = 0;
    }
    return asset;
  };

  /**
   * Toggle asset detail vendor type.
   *
   * @param {Object} assetDetail
   *
   */
  const toggleAssetDetailVendorType = ({ assetDetail }) => {
    const asset = assetDetail;
    if (!asset.vendor.type) {
      asset.vendor.idContact = '';
      asset.vendor.type = 0;
      asset.vendor.name = '';
      asset.vendor.phoneCode = '';
      asset.vendor.phone = '';
      asset.vendor.faxCode = '';
      asset.vendor.fax = '';
      asset.vendor.email = '';
      asset.vendor.address = Address.blank();
      asset.vendor.contactFirstName = '';
      asset.vendor.contactSurname = '';
    }
    return asset;
  };

  /**
   * Asset detail change.
   *
   * @param {Array} assetDetails
   * @param {Object} assetDetail
   * @param {String} action
   * @param {Boolean} isCommercial
   *
   * @returns {Object}
   */
  const assetDetailChange = ({ assetDetails, assetDetail, action, isCommercial }) => {
    const idx = helperService.findIndexById(assetDetails, assetDetail?.id);
    const newAssetDetails = assetDetails;
    if (idx > -1) {
      let asset = assetDetail;
      switch (action) {
        case 'cost':
          asset.primaryCost = assetDetail?.cost || 0;
          break;
        case 'type':
          asset = toggleAssetDetailType({
            assetDetail,
            isCommercial
          });
          break;
        case 'trade':
          asset = toggleAssetDetailTrade({
            assetDetail
          });
          break;
        case 'payout':
          asset = toggleAssetDetailPayout({
            assetDetail
          });
          break;
        case 'vendorType':
          asset = toggleAssetDetailVendorType({
            assetDetail
          });
          break;
        default:
          break;
      }
      newAssetDetails[idx] = asset;
    }
    return newAssetDetails;
  };

  /**
   * Set initial value of the ABN registered for products search.
   *
   * @param {Object} company
   *
   * @returns {Number}
   */
  const setInitialValueOfAbnRegistered = ({ company }) => {
    let abnRegistered = -1;
    if (company?.company?.information?.dateOfRegistration) {
      const abnMonths = moment
        .utc()
        .diff(
          moment.utc(company.company.information.dateOfRegistration, 'X').toDate(),
          'months'
        );
      if (abnMonths === 0) {
        abnRegistered = 0;
      } else if (abnMonths > 0 && abnMonths <= 11) {
        abnRegistered = 11;
      } else if (abnMonths >= 12 && abnMonths <= 23) {
        abnRegistered = 12;
      } else if (abnMonths >= 24 && abnMonths <= 35) {
        abnRegistered = 24;
      } else if (abnMonths >= 36 && abnMonths <= 59) {
        abnRegistered = 36;
      } else if (abnMonths >= 60) {
        abnRegistered = 60;
      }
    }
    return abnRegistered;
  };

  /**
   * Set initial value of the GST registered for products search.
   *
   * @param {Object} company
   *
   * @returns {Number}
   */
  const setInitialValueOfGstRegistered = ({ company }) => {
    let gstRegistered = 0;
    if (company?.company?.information?.isVatRegistered) {
      let gstMonths = 0;
      if (company?.company?.information?.vatRegisteredDate) {
        gstMonths = moment
          .utc()
          .diff(
            moment.utc(company.company.information.vatRegisteredDate, 'X').toDate(),
            'months'
          );
      }
      if (gstMonths === 0) {
        gstRegistered = 0;
      } else if (gstMonths > 0 && gstMonths <= 11) {
        gstRegistered = 11;
      } else if (gstMonths >= 12 && gstMonths <= 23) {
        gstRegistered = 12;
      } else if (gstMonths >= 24 && gstMonths <= 35) {
        gstRegistered = 24;
      } else if (gstMonths >= 36 && gstMonths <= 59) {
        gstRegistered = 36;
      } else if (gstMonths >= 60) {
        gstRegistered = 60;
      }
    }
    return gstRegistered;
  };

  /**
   * Set initial value of the property ownership for products search.
   *
   * @param {Array} assets
   * @param {Array} liabilities
   *
   * @returns {String}
   */
  const setInitialValueOfPropertyOwnership = ({ assets, liabilities }) => {
    let homeOwnership = '';
    const homeOwnerships = [];
    const filteredAssets = _.filter(assets, (a) =>
      helperService.isAssetTypeOwnerOccupiedOrInvestment(a.assetType)
    );
    if (filteredAssets.length) {
      for (let a = 0; a < filteredAssets.length; a += 1) {
        const filteredLiabilities = _.filter(
          liabilities,
          (l) => l.idAsset === filteredAssets[a].id
        );
        const sumBalances = _.reduce(
          filteredLiabilities,
          (memo, num) => memo + helperService.isNumber(num.balance, true),
          0
        );
        if (sumBalances > 0) {
          const result = sumBalances / helperService.isNumber(filteredAssets[a].value, true);
          homeOwnerships.push(result);
        }
      }
    }
    if (homeOwnerships.length) {
      const maxValue = _.max(homeOwnerships);
      homeOwnership = maxValue <= 80 ? 'OWNER_W_EQUITY_GT_20' : 'OWNER_W_EQUITY_LT_20';
    }
    return homeOwnership;
  };

  /**
   * Set initial value of the LVR/LVT.
   *
   * @param {Number} amount
   * @param {Number} total
   *
   * @returns {Number}
   */
  const setLvrLvt = ({ amount, total }) => {
    if (amount && total) {
      const lvrLvt = _.multiply(_.divide(amount, total), 100);
      return helperService.round({ value: lvrLvt });
    }
    return 0;
  };

  /**
   * Get selected product fee type.
   *
   * @param {String} type
   *
   * @returns {Object}
   */
  const getSelectedProductFeeType = ({ type }) =>
    _.find(
      helperService.getProductFeeTypeRelations(),
      (p) => p?.idProductType?.toLowerCase() === type?.toLowerCase()
    );

  /**
   * Set finance proposal balloon.
   *
   * @param {Number} amount
   * @param {Number} value
   * @param {Number} percent
   *
   * @returns {Object}
   */
  const setValueOrPercent = ({ amount, value, percent }) => {
    if (!percent) {
      return value && amount ? _.multiply(_.divide(value, amount), 100) : 0;
    }
    return percent ? _.multiply(amount || 0, _.divide(percent, 100)) : 0;
  };

  /**
   * Get current associated fee type.
   *
   * @param {String} idAssociatedFee
   * @param {Boolean} isQuickTool
   *
   * @returns {Object}
   */
  const getCurrentAssociatedFeeType = ({ idAssociatedFee, isQuickTool }) => {
    const availableAssetFinanceProposalAssociatedFeeTypes =
      getAvailableAssetFinanceAssociatedFeeTypes({ isQuickTool });
    return _.find(
      availableAssetFinanceProposalAssociatedFeeTypes,
      (a) => a.id.toLowerCase() === idAssociatedFee.toLowerCase()
    );
  };

  /**
   * Set finance proposal associated fees.
   *
   * @param {Array} associatedFees
   * @param {Object} preferredProduct
   * @param {Boolean} isQuickTool
   *
   * @returns {Array}
   */
  const setFinanceProposalAssociatedFees = ({
    associatedFees,
    preferredProduct,
    isQuickTool
  }) => {
    let fees = associatedFees || [];
    const productData = isQuickTool ? preferredProduct : preferredProduct?.selectedProduct;
    if (!_.isEmpty(productData) && productData?.fee?.length) {
      for (let i = 0; i < productData.fee.length; i += 1) {
        const productFeeType = getSelectedProductFeeType({
          type: productData.fee[i]?.type
        });
        if (productFeeType?.idAssociatedFee && productData.fee[i]?.value > 0) {
          const currentFeeType = getCurrentAssociatedFeeType({
            idAssociatedFee: productFeeType?.idAssociatedFee,
            isQuickTool
          });
          if (currentFeeType) {
            const associatedFee = _.find(
              fees,
              (t) => t.feeType.id === productFeeType?.idAssociatedFee
            );
            let idx = -1;
            if (associatedFee) {
              idx = helperService.findIndexById(fees, associatedFee.id);
            } else {
              fees = financeProposalAssociatedFeeAdd({
                associatedFees: fees,
                feeType: currentFeeType,
                showToast: false
              });
              idx = fees.length - 1;
            }
            if (idx > -1) {
              fees[idx].name = productData.fee[i]?.name || '';
              fees[idx].amountWithGst = productData.fee[i]?.value || 0;
              fees[idx].isCapitalised =
                preferredProduct?.productDetails?.isUpfrontFeeCapitalised || false;
            }
          }
        }
      }
    } else {
      fees = [];
    }
    return fees;
  };

  /**
   * Get finance proposal amortization schedules payment date.
   *
   * @param {Number} idx
   * @param {Object} paymentFrequency
   * @param {Number} ticketDate
   * @param {Number} paymentDate
   * @param {Boolean} payments
   *
   * @returns {Array}
   */
  const getFinanceProposalAmortizationSchedulePaymentDate = ({
    idx,
    paymentFrequency,
    ticketDate,
    paymentDate,
    payments
  }) => {
    let frequency = 'months';
    let increment = 1;
    switch (paymentFrequency?.name) {
      case PaymentFrequency.annually.name:
        frequency = 'years';
        break;
      case PaymentFrequency.quarterly.name:
        frequency = 'quarters';
        break;
      case PaymentFrequency.fortnightly.name:
        frequency = 'weeks';
        increment = 2;
        break;
      default:
        break;
    }
    let paymentDateObject = 0;
    if (idx === 0) {
      const date = moment(ticketDate, 'X') || moment().startOf('day');
      paymentDateObject = payments ? date : date.add(increment, frequency);
    } else {
      const date = moment(paymentDate, 'X');
      paymentDateObject = payments
        ? date.add(increment, frequency)
        : date.add(increment, frequency);
    }
    return parseInt(moment.utc(paymentDateObject).format('X') || 0, 10);
  };

  /**
   * Set blank finance proposal amortization schedules.
   *
   * @param {Number} nper
   * @param {Object} paymentFrequency
   * @param {Number} ticketDate
   * @param {Boolean} payments
   *
   * @returns {Array}
   */
  const setBlankFinanceProposalAmortizationSchedules = ({
    nper,
    paymentFrequency,
    ticketDate,
    payments
  }) => {
    const amortizationSchedules = [];
    for (let i = 0; i < nper || 0; i += 1) {
      const paymentDate = getFinanceProposalAmortizationSchedulePaymentDate({
        idx: i,
        paymentFrequency,
        ticketDate,
        paymentDate: i > 0 ? amortizationSchedules[i - 1].paymentDate : 0,
        payments
      });
      amortizationSchedules.push(AssetFinanceProposalAmortizationSchedule.Blank(paymentDate));
    }
    return amortizationSchedules;
  };

  /**
   * Generate finance proposal amortization schedules.
   *
   * @param {Array} amortizationSchedules
   * @param {Number} netAmountFinanced
   * @param {Number} estimatedRepayment
   * @param {Number} rate
   * @param {Number} nper
   * @param {Number} balloonValue
   * @param {Object} paymentFrequency
   * @param {Boolean} payments
   * @param {Number} ticketDate
   *
   * @returns {Array}
   */
  const generateFinanceProposalAmortizationSchedules = ({
    amortizationSchedules,
    netAmountFinanced,
    estimatedRepayment,
    rate,
    nper,
    balloonValue,
    paymentFrequency,
    payments,
    ticketDate
  }) => {
    for (let i = 0; i < amortizationSchedules?.length; i += 1) {
      const amortizationSchedule = amortizationSchedules[i];
      amortizationSchedule.paymentDate = getFinanceProposalAmortizationSchedulePaymentDate({
        idx: i,
        paymentFrequency,
        ticketDate,
        paymentDate: i > 0 ? amortizationSchedules[i - 1].paymentDate : 0,
        payments
      });
      if (!amortizationSchedule.isRepaymentEdited) {
        amortizationSchedule.repayment = estimatedRepayment;
      }
      amortizationSchedule.beginningBalance =
        i === 0
          ? _.add(_.multiply(amortizationSchedule.repayment, nper), balloonValue)
          : _.add(
              amortizationSchedules[i - 1].endingBalance,
              amortizationSchedules[0].interestDeferred
            );
      if (payments) {
        amortizationSchedule.interestPaid =
          i === 0
            ? 0
            : _.multiply(
                _.subtract(
                  amortizationSchedule.beginningBalance,
                  amortizationSchedules[0].interestDeferred
                ),
                rate
              );
      } else {
        amortizationSchedule.interestPaid =
          i === 0
            ? _.multiply(netAmountFinanced, rate)
            : _.multiply(
                _.subtract(
                  amortizationSchedule.beginningBalance,
                  amortizationSchedules[0].interestDeferred
                ),
                rate
              );
      }
      amortizationSchedule.interestDeferred =
        i === 0
          ? _.add(
              _.subtract(_.multiply(amortizationSchedule.repayment, nper), netAmountFinanced),
              balloonValue
            )
          : _.subtract(
              amortizationSchedules[i - 1]?.interestDeferred || 0,
              payments
                ? amortizationSchedule?.interestPaid || 0
                : amortizationSchedules[i - 1]?.interestPaid || 0
            );
      amortizationSchedule.principalReduction = _.subtract(
        amortizationSchedule.repayment,
        amortizationSchedule.interestPaid
      );
      amortizationSchedule.endingBalance = _.subtract(
        _.subtract(
          i === 0
            ? _.add(_.multiply(amortizationSchedule.repayment, nper), balloonValue)
            : amortizationSchedule.beginningBalance,
          amortizationSchedule.principalReduction
        ),
        amortizationSchedules[0].interestDeferred
      );
    }
    return amortizationSchedules;
  };

  /**
   * Set finance proposal amortization schedules.
   *
   * @param {Array} amortizationSchedules
   * @param {Number} netAmountFinanced
   * @param {Number} brokerCommissionPercent
   * @param {Number} rate
   * @param {Number} nper
   * @param {Number} balloonValue
   * @param {Object} paymentFrequency
   * @param {Boolean} payments
   * @param {Number} estimatedRepayment
   * @param {Number} ticketDate
   *
   * @returns {Array}
   */
  const setFinanceProposalAmortizationSchedules = ({
    amortizationSchedules,
    netAmountFinanced,
    rate,
    nper,
    balloonValue,
    paymentFrequency,
    payments,
    estimatedRepayment,
    ticketDate
  }) => {
    let amortizationSchedulesOutput = [];
    const changedRepayments = _.filter(amortizationSchedules, (r) => r.isRepaymentEdited);
    const unchangedRepayments = _.filter(amortizationSchedules, (r) => !r.isRepaymentEdited);
    const lenNCR = changedRepayments.length;
    const lenNuCR = unchangedRepayments.length;
    if (lenNCR) {
      const sumEditedRepayments = _.reduce(
        changedRepayments,
        (memo, num) => memo + parseFloat(num?.repayment || 0),
        0
      );
      const xFactor =
        sumEditedRepayments || lenNCR
          ? _.divide(
              sumEditedRepayments,
              _.subtract(lenNCR, _.multiply(lenNCR, unchangedRepayments[0].repayment))
            )
          : 0;
      const nafX = netAmountFinanced - xFactor;
      const repaymentOfFirstIteration = ticketFactFindService.getPayment(
        rate,
        nper,
        nafX,
        balloonValue,
        payments ? 1 : 0
      );
      const amortizationSchedulesOfFirstIteration =
        generateFinanceProposalAmortizationSchedules({
          amortizationSchedules: angular.copy(amortizationSchedules),
          netAmountFinanced,
          estimatedRepayment: repaymentOfFirstIteration,
          rate,
          nper,
          balloonValue,
          paymentFrequency,
          payments,
          ticketDate
        });
      const lastEndingBalanceOfAmortizationSchedulesOfFirstIteration =
        amortizationSchedulesOfFirstIteration[amortizationSchedulesOfFirstIteration.length - 1]
          .endingBalance;
      const yFactor = payments
        ? _.divide(
            lastEndingBalanceOfAmortizationSchedulesOfFirstIteration,
            _.subtract(lenNuCR, 1)
          )
        : _.divide(lastEndingBalanceOfAmortizationSchedulesOfFirstIteration, lenNuCR);
      const repaymentOfSecondIteration = _.add(repaymentOfFirstIteration, yFactor);
      const amortizationSchedulesOfSecondIteration =
        generateFinanceProposalAmortizationSchedules({
          amortizationSchedules: angular.copy(amortizationSchedules),
          netAmountFinanced,
          estimatedRepayment: repaymentOfSecondIteration,
          rate,
          nper,
          balloonValue,
          paymentFrequency,
          payments,
          ticketDate
        });
      const lastEndingBalanceOfAmortizationSchedulesOfSecondIteration =
        amortizationSchedulesOfSecondIteration[
          amortizationSchedulesOfSecondIteration.length - 1
        ].endingBalance;
      const newRepayment = _.add(
        repaymentOfFirstIteration,
        _.divide(
          _.multiply(
            _.subtract(balloonValue, lastEndingBalanceOfAmortizationSchedulesOfFirstIteration),
            _.subtract(repaymentOfSecondIteration, repaymentOfFirstIteration)
          ),
          _.subtract(
            lastEndingBalanceOfAmortizationSchedulesOfSecondIteration,
            lastEndingBalanceOfAmortizationSchedulesOfFirstIteration
          )
        )
      );
      amortizationSchedulesOutput = generateFinanceProposalAmortizationSchedules({
        amortizationSchedules,
        netAmountFinanced,
        estimatedRepayment: newRepayment,
        rate,
        nper,
        balloonValue,
        paymentFrequency,
        payments,
        ticketDate
      });
    } else {
      amortizationSchedulesOutput = generateFinanceProposalAmortizationSchedules({
        amortizationSchedules,
        netAmountFinanced,
        estimatedRepayment,
        rate,
        nper,
        balloonValue,
        paymentFrequency,
        payments,
        ticketDate
      });
    }
    return amortizationSchedulesOutput;
  };

  /**
   * Set finance proposal amortization schedules ballon section.
   *
   * @param {Array} amortizationSchedules
   * @param {Number} netAmountFinanced
   * @param {Number} brokerCommissionPercent
   * @param {Number} rate
   * @param {Number} nper
   * @param {Number} balloonValue
   * @param {Object} paymentFrequency
   * @param {Boolean} payments
   * @param {Number} estimatedRepayment
   * @param {Number} ticketDate
   *
   * @returns {Object}
   */
  const setFinanceProposalAmortizationSchedulesBallonSection = ({
    amortizationSchedules,
    rate,
    balloonValue,
    paymentFrequency,
    payments,
    ticketDate
  }) => {
    let ballonDate = 0;
    let ballonBeginningBalance = 0;
    let ballonRepayment = 0;
    let ballonInterestPaid = 0;
    let ballonInterestDeferred = 0;
    let ballonPrincipalReduction = 0;
    let ballonEndingBalance = 0;

    if (amortizationSchedules?.length && balloonValue && ticketDate) {
      let paymentDate =
        amortizationSchedules[amortizationSchedules?.length - 1]?.paymentDate || 0;
      if (payments) {
        paymentDate = getFinanceProposalAmortizationSchedulePaymentDate({
          idx: amortizationSchedules?.length || 1,
          paymentFrequency,
          ticketDate,
          paymentDate:
            amortizationSchedules[amortizationSchedules?.length - 1]?.paymentDate || 0,
          payments: false
        });
      }
      ballonDate = paymentDate
        ? dateTimeService.getFormattedDate(paymentDate, 'X')
        : moment().startOf('day');
      ballonBeginningBalance =
        amortizationSchedules[amortizationSchedules?.length - 1]?.endingBalance || 0;
      ballonRepayment = balloonValue || 0;
      ballonInterestPaid = !payments ? 0 : _.multiply(balloonValue, rate || 0);
      ballonInterestDeferred = _.subtract(
        helperService.round({
          value: amortizationSchedules[amortizationSchedules?.length - 1]?.interestDeferred || 0
        }),
        helperService.round({
          value: payments
            ? ballonInterestPaid
            : amortizationSchedules[amortizationSchedules?.length - 1]?.interestPaid || 0
        })
      );
      ballonPrincipalReduction = _.subtract(ballonRepayment, ballonInterestPaid);
      ballonEndingBalance = helperService.round({
        value: _.subtract(
          _.subtract(ballonBeginningBalance, ballonPrincipalReduction),
          ballonInterestDeferred
        )
      });
    }
    return {
      ballonDate,
      ballonBeginningBalance,
      ballonRepayment,
      ballonInterestPaid,
      ballonInterestDeferred,
      ballonPrincipalReduction,
      ballonEndingBalance
    };
  };

  /**
   * Prepare finance proposal amortization schedules.
   *
   * @param {Object} financeProposal
   * @param {NUmber} ticketDate
   *
   * @returns {Object}
   */
  const prepareFinanceProposalAmortizationSchedules = ({ financeProposal, ticketDate }) => {
    const fP = financeProposal;
    if (fP?.netAmountFinanced && fP?.interestRate && fP?.term) {
      const rate =
        fP?.estimatedBorrowerRate && fP?.paymentFrequency?.name !== PaymentFrequency.none.name
          ? _.divide(
              _.divide(fP.estimatedBorrowerRate, 100),
              helperService.getFrequencyValue(fP?.paymentFrequency)
            )
          : 0;
      const nper =
        fP?.term && fP?.paymentFrequency?.name !== PaymentFrequency.none.name
          ? _.divide(
              _.multiply(fP.term, helperService.getFrequencyValue(fP?.paymentFrequency)),
              12
            )
          : 0;

      if (nper !== fP?.amortizationSchedules?.length) {
        fP.amortizationSchedules = [];
      }

      if (!fP?.amortizationSchedules?.length) {
        fP.amortizationSchedules = setBlankFinanceProposalAmortizationSchedules({
          nper,
          paymentFrequency: fP?.paymentFrequency,
          ticketDate,
          payments: fP?.payments || false
        });
      }
      fP.amortizationSchedules = setFinanceProposalAmortizationSchedules({
        amortizationSchedules: fP?.amortizationSchedules,
        netAmountFinanced: fP?.netAmountFinanced || 0,
        rate,
        nper,
        balloonValue: fP?.balloonValue || 0,
        paymentFrequency: fP?.paymentFrequency,
        payments: fP?.payments || false,
        estimatedRepayment: fP?.estimatedRepayment || 0,
        ticketDate
      });
    } else {
      fP.amortizationSchedules = [];
    }
    return fP;
  };

  /**
   * Show finance proposal payments option by settings of selected product.
   *
   * @param {Object} selectedProduct
   * @param {String} type
   *
   * @returns {Object}
   */
  const showFinanceProposalPaymentsOptionBySettingsOfSelectedProduct = ({
    selectedProduct,
    type
  }) => {
    if (type === 'ADVANCE') {
      return selectedProduct?.assetFinance?.advance;
    }
    if (type === 'ARREARS') {
      return selectedProduct?.assetFinance?.arrears;
    }
    return true;
  };

  /**
   * Add product to compare.
   *
   * @param {Array} assetDetails
   * @param {Object} selectedProduct
   * @param {Object} searchFields
   * @param {Boolean} isCommercial
   * @param {Boolean} isNew
   *
   * @returns {Object}
   */
  const addProductToCompare = ({ selectedProduct }) => {
    let compareProduct;
    const product = JSON.parse(JSON.stringify(selectedProduct));
    if (
      product &&
      !_.isEmpty(product?.assetFinance) &&
      !_.isEmpty(product?.assetFinanceEstimate[0]?.product)
    ) {
      const productEstimate = product?.assetFinanceEstimate[0]?.product;
      let blankProduct = AssetFinanceCompareProduct.Blank();

      blankProduct = fillAssetFinanceCompareProduct(blankProduct, productEstimate);

      delete product.residential;
      delete product.residentialEstimate;
      delete product.homeLoanEstimate;
      delete product.assetFinanceEstimate;
      delete product.docType;

      blankProduct.selectedProduct = product;

      compareProduct = AssetFinanceCompareProduct.Build(blankProduct);
    } else {
      compareProduct = AssetFinanceCompareProduct.Blank();
    }
    return compareProduct;
  };

  /**
   * Are all required compliance fields filled.
   *
   * @param {Object} compliance
   * @param {Boolean} isCommercial
   *
   * @returns {Boolean}
   */
  const areAllRequiredComplianceFieldsFilled = ({ compliance, isCommercial }) => {
    if (compliance?.comments && !isCommercial) {
      return (
        compliance?.comments?.livingExpenses !== '' &&
        compliance?.comments?.backgroundPurpose !== '' &&
        compliance?.comments?.abilityToServiceFinancialAnalysis !== '' &&
        compliance?.comments?.lenderAndProductSelectionJustification !== '' &&
        compliance?.comments?.general !== ''
      );
    }
    return true;
  };

  /**
   * Set initial upfront commission.
   *
   * @param {Number} upfront
   * @param {Number} upfrontPercentage
   * @param {Number} brokerCommissionValue
   *
   * @returns {Number}
   */
  const setCommissionUpfront = ({ upfront, upfrontPercentage, brokerCommissionValue }) =>
    upfront !== 0
      ? upfront
      : _.multiply(
          brokerCommissionValue,
          upfrontPercentage ? _.divide(upfrontPercentage, 100) : 0
        );

  /**
   * Set initial trail commission.
   *
   * @param {Number} trail
   * @param {Number} trailPercentage
   * @param {Number} brokerFee
   *
   * @returns {Number}
   */
  const setCommissionTrail = ({ trail, trailPercentage, brokerFee }) =>
    trail !== 0
      ? trail
      : _.multiply(brokerFee, trailPercentage ? _.divide(trailPercentage, 100) : 0);

  /**
   * Set initial split upfront commission.
   *
   * @param {Number} splitUpfront
   * @param {Number} splitUpfrontPercentage
   * @param {Object} parentSplit
   * @param {Boolean} isRemainder
   *
   * @returns {Number}
   */
  const setCommissionSplitUpfront = ({
    splitUpfront,
    splitUpfrontPercentage,
    parentSplit,
    isRemainder
  }) => {
    if (isRemainder !== true) {
      return splitUpfront !== 0
        ? splitUpfront
        : _.multiply(parentSplit.upfront, _.divide(splitUpfrontPercentage, 100));
    }
    const total = parentSplit.upfront;
    let sum = 0;
    if (parentSplit.splits.length) {
      sum = parentSplit.splits.reduce(
        (memo, sp) =>
          _.add(memo, _.isNumber(_.toNumber(sp?.upfront)) ? _.toNumber(sp?.upfront) : 0),
        0
      );
    }
    return _.subtract(total, sum);
  };

  /**
   * Set initial split upfront commission.
   *
   * @param {Number} splitUpfront
   * @param {Number} splitUpfrontPercentage
   * @param {Object} parentSplit
   * @param {Boolean} isRemainder
   *
   * @returns {Number}
   */
  const setCommissionSplitTrail = ({
    splitTrail,
    splitTrailPercentage,
    parentSplit,
    isRemainder
  }) => {
    if (isRemainder !== true) {
      return splitTrail !== 0
        ? splitTrail
        : _.multiply(parentSplit.trail, _.divide(splitTrailPercentage, 100));
    }
    const total = parentSplit.trail;
    let sum = 0;
    if (parentSplit?.splits?.length) {
      sum = _.reduce(
        parentSplit?.splits || [],
        (memo, sp) =>
          _.add(memo, _.isNumber(_.toNumber(sp?.trail)) ? _.toNumber(sp?.trail) : 0),
        0
      );
    }
    return _.subtract(total, sum);
  };

  /**
   * Set commission.
   *
   * @param {Object} commission
   * @param {Object} financeProposal
   * @param {Array} commissions
   * @param {Boolean} isInitial
   *
   * @returns {Object}
   */
  const setCommission = ({ commission, financeProposal, commissions, isInitial }) => {
    const cms = commission;
    cms.commissionSplits = [];
    let selectedCommission = {};
    if (isInitial) {
      selectedCommission = commissions?.length ? commissions[1] : {};
    } else {
      selectedCommission = _.find(commissions, (c) => c.id === cms.idCommission);
    }
    if (!_.isEmpty(selectedCommission) && !_.isEmpty(financeProposal)) {
      if (isInitial) {
        cms.idCommission = selectedCommission.id;
      }
      cms.commissionSplits = angular.copy(selectedCommission.splits);
      for (let i = 0; i < cms.commissionSplits.length; i += 1) {
        cms.commissionSplits[i].upfront = setCommissionUpfront({
          upfront: _.isNumber(_.toNumber(cms.commissionSplits[i]?.upfront))
            ? _.toNumber(cms.commissionSplits[i].upfront)
            : 0,
          upfrontPercentage: _.isNumber(_.toNumber(cms.commissionSplits[i]?.upfrontPercentage))
            ? _.toNumber(cms.commissionSplits[i].upfrontPercentage)
            : 0,
          brokerCommissionValue: _.isNumber(_.toNumber(financeProposal?.brokerCommissionValue))
            ? _.toNumber(financeProposal?.brokerCommissionValue)
            : 0
        });
        cms.commissionSplits[i].trail = setCommissionTrail({
          trail: _.isNumber(_.toNumber(cms.commissionSplits[i]?.trail))
            ? _.toNumber(cms.commissionSplits[i]?.trail)
            : 0,
          trailPercentage: _.isNumber(_.toNumber(cms.commissionSplits[i].trailPercentage))
            ? _.toNumber(cms.commissionSplits[i].trailPercentage)
            : 0,
          brokerFee: _.isNumber(_.toNumber(financeProposal?.brokerFee))
            ? _.toNumber(financeProposal?.brokerFee)
            : 0
        });

        for (let j = 0; j < cms.commissionSplits[i].splits.length; j += 1) {
          cms.commissionSplits[i].splits[j].upfront = setCommissionSplitUpfront({
            splitUpfront: _.isNumber(_.toNumber(cms.commissionSplits[i]?.splits[j]?.upfront))
              ? _.toNumber(cms.commissionSplits[i].splits[j].upfront)
              : 0,
            splitUpfrontPercentage: _.isNumber(
              _.toNumber(cms.commissionSplits[i]?.splits[j]?.upfrontPercentage)
            )
              ? _.toNumber(cms.commissionSplits[i].splits[j].upfrontPercentage)
              : 0,
            parentSplit: cms.commissionSplits[i] || [],
            isRemainder: cms.commissionSplits[i]?.splits[j]?.isRemainder || false
          });
          cms.commissionSplits[i].splits[j].trail = setCommissionSplitTrail({
            splitTrail: _.isNumber(_.toNumber(cms.commissionSplits[i]?.splits[j]?.trail))
              ? _.toNumber(cms.commissionSplits[i].splits[j].trail)
              : 0,
            splitTrailPercentage: _.isNumber(
              _.toNumber(cms.commissionSplits[i]?.splits[j]?.trailPercentage)
            )
              ? _.toNumber(cms.commissionSplits[i].splits[j].trailPercentage)
              : 0,
            parentSplit: cms.commissionSplits[i] || [],
            isRemainder: cms.commissionSplits[i]?.splits[j]?.isRemainder || false
          });

          for (let k = 0; k < cms.commissionSplits[i].splits[j].splits.length; k += 1) {
            cms.commissionSplits[i].splits[j].splits[k].upfront = setCommissionSplitUpfront({
              splitUpfront: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.upfront)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].upfront)
                : 0,
              splitUpfrontPercentage: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.upfrontPercentage)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].upfrontPercentage)
                : 0,
              parentSplit: cms.commissionSplits[i]?.splits[j] || [],
              isRemainder: cms.commissionSplits[i]?.splits[j]?.splits[k]?.isRemainder || false
            });
            cms.commissionSplits[i].splits[j].splits[k].trail = setCommissionSplitTrail({
              splitTrail: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.trail)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].trail)
                : 0,
              splitTrailPercentage: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.trailPercentage)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].trailPercentage)
                : 0,
              parentSplit: cms.commissionSplits[i]?.splits[j] || [],
              isRemainder: cms.commissionSplits[i]?.splits[j]?.splits[k]?.isRemainder || false
            });
          }
        }
      }
    }
    return cms;
  };

  const hasBasisForRecommendationFilled = ({ compliance }) =>
    compliance?.basisForRecommendationsMade?.paymentFrequency &&
    (compliance?.basisForRecommendationsMade?.financeTerm?.longer ||
      compliance?.basisForRecommendationsMade?.financeTerm?.earlier);

  return {
    createAssetFinance,
    readAssetFinance,
    updateAssetFinance,
    updateAssetFinancePartial,
    deleteAssetFinance,
    getAvailableAssetTypes,
    getAvailableAssetAges,
    getAvailableCarTypes,
    getAvailableVendorTypes,
    getAvailableProductTypes,
    getAssetFinanceInsuranceProductTypes,
    getAssetFinanceInterestRates,
    getAssetFinanceCreditRecords,
    getAvailablePaymentFrequencies,
    getAvailableSearchAssetAges,
    getAvailableGrossVehicleMass,
    getAvailablePayLoad,
    getAvailablePax,
    getAvailableLoanTypes,
    getAvailableYesNoOption,
    getAvailableElapsedTimeFromPayout,
    getAvailableElapsedTermOldLoan,
    getAvailableIncreaseInRepayment,
    getAvailableAssetFinanceEmploymentBasis,
    getAvailableTimeAtCurrentAddress,
    getAvailableCreditHistories,
    getAvailableResidentialStatus,
    getAvailableAbnRegistered,
    getAvailableGstRegistered,
    getAvailableHomeOwnership,
    getAvailableTimeInEmployment,
    getAvailableBallonPaymentDebtOptions,
    getAvailableBankrupts,
    getAvailableFinancialDefaults,
    getAvailableUtilityAndTelCo,
    getAvailablePayDayEnquires,
    getAvailablePayDayOnStatements,
    getAvailableDepositRequired,
    getAvailableMaxNegativeEquity,
    getAvailableAssetFinanceInsuranceTypes,
    getAvailableAssetFinanceAssociatedFeeTypes,
    getProductType,
    getLoanTerm,
    assetDetailsAdd,
    additionalProductsAdd,
    financeProposalAssociatedFeeAdd,
    assetCommitmentScheduleAdd,
    assetCommitmentAdd,
    getAssetCommitmentScheduleTotal,
    changeSelectedItemOfVendor,
    getUpfrontFee,
    getMonthlyFee,
    getCompound,
    getRate,
    getNetAmountFinanced,
    getCurrentNetAmountFinanced,
    getNumberOfPaymentPeriods,
    getRateWithoutCompound,
    getPreferredProduct,
    getProductTotalFees,
    isAnzSelectedAsLender,
    isMacqSelectedAsLender,
    isMacqSelectedAsLenderAndFeeTypeMarkedAsPpsr,
    isProductTypeLeaseAndAnzLender,
    showEditableFields,
    canSelectLease,
    isLeaseSelectedAsProductType,
    assetDetailChange,
    showAssetDetailMileageOrHours,
    setInitialValueOfAbnRegistered,
    setInitialValueOfGstRegistered,
    setInitialValueOfPropertyOwnership,
    setLvrLvt,
    setValueOrPercent,
    setFinanceProposalAssociatedFees,
    showFinanceProposalPaymentsOptionBySettingsOfSelectedProduct,
    getFinanceProposalAmortizationSchedulePaymentDate,
    setBlankFinanceProposalAmortizationSchedules,
    setFinanceProposalAmortizationSchedules,
    setFinanceProposalAmortizationSchedulesBallonSection,
    prepareFinanceProposalAmortizationSchedules,
    addProductToCompare,
    areAllRequiredComplianceFieldsFilled,
    setCommission,
    hasBasisForRecommendationFilled
  };
};
